/**
 * VendorAutocomplete — reusable vendor search-and-pick input.
 *
 * 2026-02-19 — spun up for the "prevent duplicate vendors + save typing"
 * initiative. Replaces plain <select> vendor dropdowns in:
 *   · Procurement · New PO modal          (ha-po-vendor)
 *   · Custom HA Orders · New Order modal  (ha-cha-vendor-id)
 *   · Product Catalogue · Edit modal      (ha-pf-vendor, optional)
 *
 * Props
 * -----
 *   vendors    — array of {vendor_id, name, state?, gstin?, contact_person?, …}
 *                already loaded by the parent (we do NOT re-fetch to keep this
 *                light; backend GET /api/vendors is cheap enough for a parent
 *                to prefetch once per modal open).
 *   value      — currently-selected vendor_id, or '' / null for "no pick yet".
 *   onChange   — called with the picked vendor_id (empty string when the input
 *                is cleared).
 *   onCreateNew — optional callback for the "+ Add new vendor" footer action.
 *                If omitted, the footer is hidden.
 *   placeholder — override the default placeholder.
 *   disabled   — grey-out the input.
 *   testid     — root data-testid (e.g. "ha-po-vendor"). Suggestions are
 *                tagged `${testid}-suggestion-${i}` for Playwright.
 *   allowClear — show an inline × to clear the picked vendor (default true).
 *
 * Behaviour
 * ---------
 *   · ≥2 chars before suggestions appear (per the user brief).
 *   · case-insensitive partial match from anywhere in the name — plus
 *     secondary matches on contact_person / phone / GSTIN / state so a user
 *     who remembers "Delhi vendor" finds it even if the name is in English.
 *   · Keyboard: ArrowUp / ArrowDown to navigate, Enter to pick, Esc to close.
 *   · Clicking a suggestion picks it. Clicking outside closes the dropdown.
 *   · Picked vendor is rendered as its full name in the input (not the id),
 *     so the operator sees the human label even on reload.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus, X, Search } from 'lucide-react';

const normalise = (s) => (s || '').toString().toLowerCase().trim();


export default function VendorAutocomplete({
  vendors = [],
  value = '',
  onChange,
  onCreateNew = null,
  placeholder = 'Search vendor by name, city or GSTIN…',
  disabled = false,
  testid = 'vendor-autocomplete',
  allowClear = true,
  // 2026-02-19 — `returnFormat` picks what the parent gets back in onChange.
  //   "id"   (default) → onChange(vendor_id)      — most call-sites
  //   "name" → onChange(vendor_name)              — StockRequestsPage.jsx
  //                                                 "Mark PO sent to vendor"
  //                                                 flow where the backend
  //                                                 stores a free-text name,
  //                                                 not a vendor_id.
  returnFormat = 'id',
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Keep the visible text in sync with the picked vendor so the operator
  // sees "Oticon India Pvt Ltd" instead of a blank input after reload.
  // In "name" mode the `value` prop is already the vendor name string, so
  // the picked lookup matches by name instead of vendor_id.
  const picked = useMemo(
    () => vendors.find((v) =>
      returnFormat === 'name' ? v.name === value : v.vendor_id === value,
    ) || null,
    [vendors, value, returnFormat],
  );
  useEffect(() => {
    if (!open) {
      // In name-mode a stale value (vendor renamed / deleted) still needs
      // to be visible to the operator — fall back to the raw string.
      if (picked) setQuery(picked.name || '');
      else if (returnFormat === 'name' && value) setQuery(String(value));
      else setQuery('');
    }
  }, [picked, open, returnFormat, value]);

  // Filter: ≥2 chars, case-insensitive, partial match anywhere.
  const suggestions = useMemo(() => {
    const q = normalise(query);
    if (q.length < 2) return [];
    const scored = [];
    for (const v of vendors) {
      const name = normalise(v.name);
      if (!name) continue;
      const matchName = name.includes(q);
      const matchSecondary =
        normalise(v.city).includes(q) ||
        normalise(v.state).includes(q) ||
        normalise(v.gstin).includes(q) ||
        normalise(v.contact_person).includes(q) ||
        normalise(v.phone).includes(q);
      if (!matchName && !matchSecondary) continue;
      // Rank: name-startsWith > name-contains > secondary-only.
      let rank = 2;
      if (name.startsWith(q)) rank = 0;
      else if (matchName) rank = 1;
      scored.push({ v, rank });
    }
    scored.sort((a, b) => a.rank - b.rank || a.v.name.localeCompare(b.v.name));
    return scored.slice(0, 20).map((s) => s.v);
  }, [query, vendors]);

  // Reset highlight whenever the suggestion list changes shape.
  useEffect(() => { setHighlight(0); }, [query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        // Restore the picked name on blur so a half-typed query doesn't
        // visually "lose" the selection.
        if (picked) setQuery(picked.name || '');
        else setQuery('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, picked]);

  const pick = (v) => {
    if (!v) return;
    onChange?.(returnFormat === 'name' ? (v.name || '') : v.vendor_id);
    setQuery(v.name || '');
    setOpen(false);
    inputRef.current?.blur();
  };

  // In name-mode the operator typing free-text should ALSO update the
  // parent (we're replacing a free <input>, so the parent expects
  // keystroke-level updates). Picking a suggestion then overrides to the
  // canonical casing. In id-mode we never emit free text — the parent
  // only ever gets a vendor_id or empty string.
  const onInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    if (returnFormat === 'name') {
      onChange?.(v);
    }
  };

  const clear = () => {
    onChange?.('');
    setQuery('');
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions[highlight]) pick(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const showEmpty = open && normalise(query).length >= 2 && suggestions.length === 0;

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <div className={`flex items-center border rounded ${disabled ? 'bg-slate-50 border-slate-200' : 'border-slate-300 bg-white'}`}>
        <Search size={12} className="ml-2 text-slate-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={onInputChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={`${testid}-input`}
          autoComplete="off"
          className="flex-1 px-2 py-1 text-sm bg-transparent focus:outline-none disabled:cursor-not-allowed"
        />
        {picked && allowClear && !disabled && (
          <button
            type="button"
            onClick={clear}
            data-testid={`${testid}-clear`}
            title="Clear vendor"
            className="mr-1 w-5 h-5 rounded hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
          ><X size={11} /></button>
        )}
      </div>

      {open && (suggestions.length > 0 || showEmpty) && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded shadow-xl z-50 max-h-72 overflow-auto"
        >
          {suggestions.map((v, i) => {
            const isPicked = v.vendor_id === value;
            const isHl = i === highlight;
            // Secondary line prefers city (added 2026-02-19 to close the
            // "Oticon India · Bengaluru" wording gap flagged in the original
            // brief). Falls through to state → GSTIN → contact → phone so
            // legacy vendors without a city still render a useful hint.
            const geo = v.city || v.state || '';
            const second = geo ||
              (v.gstin ? `GSTIN ${v.gstin}` : '') ||
              v.contact_person || v.phone || '';
            return (
              <button
                type="button"
                key={v.vendor_id}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(v)}
                data-testid={`${testid}-suggestion-${i}`}
                className={`w-full text-left px-3 py-1.5 border-b border-slate-100 last:border-b-0 flex items-start gap-2 ${
                  isHl ? 'bg-indigo-50' : ''
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{v.name}</div>
                  {second && <div className="text-[10.5px] text-slate-500 truncate">{second}</div>}
                </div>
                {isPicked && <Check size={13} className="text-indigo-600 mt-1 shrink-0" />}
              </button>
            );
          })}
          {showEmpty && (
            <div className="px-3 py-2 text-[11px] text-slate-500 italic" data-testid={`${testid}-empty`}>
              No vendor matches "{query}".
              {onCreateNew && (
                <> Press the <b>+ Add new vendor</b> below to create one.</>
              )}
            </div>
          )}
          {onCreateNew && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onCreateNew(); setOpen(false); }}
              data-testid={`${testid}-create-new`}
              className="w-full text-left px-3 py-1.5 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-50 bg-slate-50 border-t border-slate-200 flex items-center gap-1"
            ><Plus size={11} /> Add new vendor…</button>
          )}
        </div>
      )}
    </div>
  );
}
